import React, { createContext, useContext, useEffect, useState } from "react";
import { Dialog } from "@material-ui/core";
import { useBaseData } from "./BaseDataProvider.jsx";
import { useHttp } from "../Common/HttpProvider.jsx";
import NewsContent from "../../Pages/Home/News/NewsContent.jsx";
import { newsActionTypes } from "../../constants.js";

/**
 * @typedef {Object} NewsProviderContext
 * @property {TheNews[]} news
 * @property {Number} unwatchedCount
 * @property {FnAsyncNumberVoid} watchNews
 */

/** @type {React.Context<NewsProviderContext>} */
const newsContext = createContext({
  news: [],
  unwatchedCount: 0,
  /** @type {FnAsyncNumberVoid} */ watchNews: () => {},
});

/** @returns {NewsProviderContext} */
export const useNews = () => useContext(newsContext);

/**
 * @param {React.ReactNode} children
 * @returns {React.ReactNode}
 * @constructor
 */
const NewsProvider = ({ children }) => {
  const { news, setNews } = useBaseData();
  const { post, get } = useHttp();
  const [unwatchedNews, setUnwatchedNews] = useState([]);
  const [theNews, setTheNews] = useState(/** @type {?TheNews} */ null);

  // фильтруем новости, находим непоказанные, устанавливаем unwatchedNews
  useEffect(() => {
    if (!news) return;

    if (news.length > 0) {
      const unwatched = news.filter((item) => item.mustBeRead);
      setUnwatchedNews(unwatched);
    }
  }, [news]);

  // Если есть непрочитанные новости, то устанавливаем в текущую новость первую из массива
  useEffect(() => {
    if (unwatchedNews.length > 0) {
      setTheNews(unwatchedNews[0]);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unwatchedNews]);

  /** @type {FnAsyncNumberVoid} */
  const watchNews = async (id, type) => {
    if (!id || !type) {
      return;
    }
    await post("news/user_reads", { newsId: id, actionType: type }, null, null, true);
    if (type === newsActionTypes.read) {
      const newUnwatched = unwatchedNews.filter((item) => item.id !== id);
      if (newUnwatched.length > 0) {
        setUnwatchedNews(newUnwatched);
      } else {
        const newNews = await get("news");
        if (newNews) {
          setNews(newNews);
        }
      }
    }
  };

  /** */
  const onCloseNewsDialog = async () => {
    await watchNews(theNews.id, newsActionTypes.read);
    setTheNews(null);
  };

  const value = {
    news,
    unwatchedNews,
    watchNews,
  };

  return (
    <newsContext.Provider value={value}>
      {children}
      {theNews && (
        <Dialog open={!!theNews} onClose={onCloseNewsDialog} scroll="body" fullWidth maxWidth="md">
          <NewsContent theNews={theNews} onClose={onCloseNewsDialog} />
        </Dialog>
      )}
    </newsContext.Provider>
  );
};

export default NewsProvider;
